import apiClient from 'apiClient';
import { ValidationResponse, ValidationStatuses } from './types';
import cachedValidateRequest from 'modules/business_rules/api/cachedValidateRequest';
import { ProductRepaymentType, ProductType } from 'domain/Product';
import { Nullable } from 'utils/types';

export enum ValidationErrorReason {
  TOO_OLD = 'TOO_OLD',
  TOO_YOUNG = 'TOO_YOUNG',
  IN_ACCEPTABLE_RANGE = 'IN_ACCEPTABLE_RANGE',
}

interface Params {
  reason: ValidationErrorReason;
  inAcceptableRange: {
    years: number;
    months: number;
  };
  minAcceptableYears: number;
  maxAcceptableYears: number;
}

export class ApplicantDateOfBirthValidationError extends Error {
  params: Nullable<Params> = null;

  constructor(message: any, params: any) {
    super(message);
    this.params = params;
  }

  getParams() {
    return this.params;
  }

  getReason(): Nullable<ValidationErrorReason> {
    return this.params ? this.params.reason : null;
  }

  getMinAcceptableYears(): Nullable<number> {
    return this.params ? this.params.minAcceptableYears : null;
  }

  getMaxAcceptableYears(): Nullable<number> {
    return this.params ? this.params.maxAcceptableYears : null;
  }

  getInAcceptableYears(): Nullable<number> {
    return this.params ? this.params.inAcceptableRange.years : null;
  }

  getInAcceptableMonths(): Nullable<number> {
    return this.params ? this.params.inAcceptableRange.months : null;
  }

  buildMessage() {
    if (this.getReason() === ValidationErrorReason.TOO_OLD) {
      return `We are only able to accept applications from borrowers up to ${this.getMaxAcceptableYears()} years of age`;
    }
    if (this.getReason() === ValidationErrorReason.TOO_YOUNG) {
      return `We are only able to accept applications from borrowers aged at least ${this.getMinAcceptableYears()} years old`;
    }
    if (this.getReason() === ValidationErrorReason.IN_ACCEPTABLE_RANGE) {
      return `We accept applications from applicants who are ${this.getInAcceptableYears()}yrs ${this.getInAcceptableMonths()}mths, but they will have to be ${this.getMinAcceptableYears()} prior to completion`;
    }
    return 'We are only able to accept applications from over 50 year old\'s, as per FCA retirement interest only mortgage guidelines.';
  }
}

const validate = async (
  dateOfBirth: string,
  productType: ProductType,
  repaymentType: Nullable<ProductRepaymentType>,
): Promise<ValidationResponse> => {
  const response = await apiClient.post('/api/v1/business-rules/validate-applicant-age', {
    dateOfBirth,
    productType,
    repaymentType,
  });

  if (response.data.status !== ValidationStatuses.OK) {
    throw new ApplicantDateOfBirthValidationError(response.data.details, response.data.params);
  }

  return response.data;
};

const cachedValidate = async (
  dateOfBirth: string,
  productType: ProductType,
  repaymentType: Nullable<ProductRepaymentType>,
) => {
  return cachedValidateRequest(async () => {
    return await validate(dateOfBirth, productType, repaymentType);
  }, `validate-applicant-date-of-birth-${dateOfBirth}-${productType}-${repaymentType}`);
};

const exportObject = { validate, cachedValidate };

export default exportObject;
